import SearchIcon from '@mui/icons-material/Search';
import { InputAdornment, ListItemText, MenuList, Popover, TextField } from '@mui/material';
import MenuItem from '@mui/material/MenuItem';
import {
  menuOpeningBottomLeftProps,
  menuOpeningProps,
  menuOpeningTopLeftProps,
} from 'components/brand-voice/menu-opening-position-props';
import { InfiniteDropdownIntersectionObserver } from 'components/InfiniteDropdownIntersectionObserver';
import { EMPTY_OPTION } from 'features/brand-voice/components/AutocompleteDropdownMenuContent';
import { BrandVoiceMenuItemContent } from 'features/brand-voice/components/BrandVoiceMenuItemContent';
import { BrandVoiceSelectRenderValue } from 'features/brand-voice/components/BrandVoiceSelectRenderValue';
import { CreateBrandVoiceMenuItemContent } from 'features/brand-voice/components/CreateBrandVoiceMenuItemContent';
import { EmptyBrandVoiceMenuItemContent } from 'features/brand-voice/components/EmptyBrandVoiceMenuItemContent';
import { useBrandVoiceAutocompleteData } from 'features/brand-voice/useBrandVoiceAutocompleteData';
import { useRedirectToBrandVoiceCreation } from 'features/brand-voice/useRedirectToBrandVoiceCreation';
import { useSetDefaultBrandVoiceMutation } from 'features/brand-voice/useSetDefaultBrandVoiceMutation';
import { MouseEvent, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { ModelsBrandVoice } from 'services/backofficeIntegration/http/endpoints/new-brand-voice/httpCreateBrandVoice';
import gtmIds from 'services/tracking/GTMIds';
import { withGtmInteraction } from 'services/tracking/withGtmInteraction';
import styled from 'styled-components';
import { useDebounce } from 'use-debounce';

type Props = {
  selectedBrandVoiceId?: string;
  targetLanguage?: string;
  targetCountry?: string;
  menuOpeningDirection?: 'top' | 'bottom';
  gtmId?: string;

  onMouseDown?: (event: MouseEvent) => void;
  onBrandVoiceChange?: (brandVoiceId: string | null) => void | boolean | Promise<void | boolean>;
};

export const ExtendedChatInputBrandVoiceSelect = ({
  selectedBrandVoiceId,
  targetLanguage,
  targetCountry,
  menuOpeningDirection = 'bottom',
  gtmId,
  onMouseDown,
  onBrandVoiceChange,
}: Props) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const [localSelectedBrandVoiceId, setLocalSelectedBrandVoiceId] = useState<string>(
    selectedBrandVoiceId ?? (EMPTY_OPTION.id as string)
  );

  useEffect(() => {
    setLocalSelectedBrandVoiceId(selectedBrandVoiceId ?? (EMPTY_OPTION.id as string));
  }, [selectedBrandVoiceId]);

  const redirectToBrandVoiceCreation = useRedirectToBrandVoiceCreation();

  const [search, setSearch] = useState('');
  const [debouncedSearch] = useDebounce(search, 500);

  const { brandVoices, hasNextPage, isFetchingNextPage, fetchNextPage } =
    useBrandVoiceAutocompleteData({
      initialBrandVoiceId: selectedBrandVoiceId,
      targetLanguage,
      targetCountry,
      search: debouncedSearch,
    });

  const handleTriggerClick = (event: MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setAnchorEl(event.currentTarget as HTMLElement);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleCreateBrandVoiceClick = () => {
    setAnchorEl(null);

    redirectToBrandVoiceCreation();
  };

  const { mutate: setDefaultBrandVoice } = useSetDefaultBrandVoiceMutation();

  const handleChange = async (brandVoiceId: string) => {
    setAnchorEl(null);

    if (onBrandVoiceChange) {
      const result = await onBrandVoiceChange(brandVoiceId == 'empty' ? null : brandVoiceId);
      if (result === false) {
        return;
      }
    }

    setLocalSelectedBrandVoiceId(brandVoiceId);

    if (brandVoiceId !== 'empty') {
      setDefaultBrandVoice({ brandVoiceId });
    }
  };

  const handleBrandVoiceSelect = (brandVoice: ModelsBrandVoice) => {
    const brandVoiceId = (brandVoice.id ?? EMPTY_OPTION.id) as string;
    handleChange(brandVoiceId);
  };

  return (
    <>
      <TriggerWrapper {...withGtmInteraction(gtmId ?? gtmIds.aiWriter.chat.openBrandVoiceSelect)}>
        <BrandVoiceSelectRenderValue
          brandVoices={brandVoices}
          selectedId={localSelectedBrandVoiceId}
          defaultMessage="-"
          onClick={handleTriggerClick}
          onMouseDown={onMouseDown}
        />
      </TriggerWrapper>

      <StyledPopover
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        {...menuOpeningProps[menuOpeningDirection]}
      >
        <StyledTextField
          variant="standard"
          fullWidth
          value={search}
          onChange={e => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
        <StyledMenuList>
          {search.length === 0 && (
            <>
              <StyledMenuItem onClick={handleCreateBrandVoiceClick}>
                <CreateBrandVoiceMenuItemContent />
              </StyledMenuItem>

              <StyledMenuItem
                key={EMPTY_OPTION.id}
                onClick={() => handleChange('empty')}
                selected={
                  !localSelectedBrandVoiceId || localSelectedBrandVoiceId === EMPTY_OPTION.id
                }
              >
                <EmptyBrandVoiceMenuItemContent />
              </StyledMenuItem>
            </>
          )}

          {brandVoices.length === 0 && (
            <StyledMenuItem disabled>
              <ListItemText>
                <FormattedMessage id="aiWriter.chat.brandVoice.selector.noBrandVoice" />
              </ListItemText>
            </StyledMenuItem>
          )}

          {brandVoices.map(brandVoice => (
            <StyledMenuItem
              key={brandVoice.id ?? Math.random()}
              value={brandVoice.id || ''}
              onClick={() => handleBrandVoiceSelect(brandVoice)}
              selected={localSelectedBrandVoiceId === brandVoice.id}
            >
              <BrandVoiceMenuItemContent
                option={brandVoice}
                searchText={search}
                isPersonal={brandVoice.personal}
              />
            </StyledMenuItem>
          ))}
        </StyledMenuList>

        <InfiniteDropdownIntersectionObserver
          onIsInView={() => fetchNextPage()}
          isAlreadyFetching={isFetchingNextPage}
          enabled={hasNextPage}
        />
      </StyledPopover>
    </>
  );
};

const StyledPopover = styled(Popover)`
  .MuiPaper-root {
    width: 390px;
    max-height: 280px;
    display: flex;
    flex-direction: column;
    padding: ${({ theme }) => `${theme.spacings.two} 0`};
  }
`;

const StyledMenuList = styled(MenuList)`
  overflow-y: auto;
  flex-grow: 1;
`;

const StyledTextField = styled(TextField)`
  .MuiInputBase-root {
    border: none;
    outline: none;
    box-shadow: none;
    padding: ${({ theme }) => `${theme.spacings.two} ${theme.spacings.four}`};

    &.MuiInput-root::before {
      border-bottom: 2px solid ${({ theme }) => theme.colors.divider};
    }
  }
`;

const StyledMenuItem = styled(MenuItem)`
  padding: ${({ theme }) => `${theme.spacings.two} ${theme.spacings.four}`};
`;

const TriggerWrapper = styled.div`
  max-width: 160px;
`;
